import { type MouseEvent } from 'react';
import { Box, Text, Grid, Badge } from '@simplywallst/ui-core';
import PNV from '@/components/PositiveNegativeValue';
import { Snowflake } from '@simplywallst/ui-charts/Snowflake';
import WatchlistAddRemoveCompanyToggle from '@/components/WatchlistAddRemoveCompanyToggle';
import { CompanyIcon } from '@/components/CompanyIcon';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useTracking } from 'react-tracking';
import { getCloudflareImageUrl } from '@/hooks/useCloudflareImage';
import InfoDescriptionSolid from '@simplywallst/ui-core/icons/InfoDescriptionSolid';
import { RouterLink } from '@/utilities/redux-first-router.export';
import { Tile } from '@/components/TileNew';
import { useResponsiveWidth } from '@/hooks/useResponsiveWidth';

type SnowflakePoints = [number, number, number, number, number];

export type Props = {
  index: number;
  companyId: string;
  companyName: string;
  canonicalUrl: string;
  uniqueSymbol: string;
  description: string;
  website: string;
  marketCap: string;
  return7D: number | null;
  return7DFormatted: string;
  return1Y: number | null;
  return1YFormatted: string;
  tickerSymbol: string;
  sharePrice: string;
  snowflakePoints: SnowflakePoints;
  industry: string;
  peRatio: string;
  growthEstimate: string;
  analystPriceTarget: string;
  isRecentSearchEntry?: boolean | null;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const CompanyTile = ({
  index,
  companyId,
  companyName,
  canonicalUrl,
  uniqueSymbol,
  description,
  website,
  marketCap,
  return7D,
  return7DFormatted,
  return1Y,
  return1YFormatted,
  tickerSymbol,
  sharePrice,
  snowflakePoints,
  industry,
  peRatio,
  growthEstimate,
  analystPriceTarget,
  isRecentSearchEntry,
  onMouseOver,
  onMouseOut,
  onFocus,
  onBlur,
}: Props) => {
  const { trackEvent } = useTracking({
    action: 'click',
    subject: 'tile',
    uniqueSymbol: uniqueSymbol,
    companyId: companyId,
    index,
  });

  const { isMobile, isTablet } = useUserAgent();
  const isMobileOrTablet = isMobile || isTablet;

  const { ref, width } = useResponsiveWidth();

  const companyIconUrl = getCloudflareImageUrl({
    src: `https://logo.clearbit.com/${website}`,
    width: 50,
    blur: 2,
  });

  return (
    <RouterLink
      to={canonicalUrl}
      noUnderline
      onClick={() => trackEvent()}
      onFocus={() => onFocus?.()}
      onBlur={() => onBlur?.()}
    >
      <Tile
        onMouseOver={() => onMouseOver?.()}
        onMouseOut={() => onMouseOut?.()}
      >
        <Tile.BackgroundImage opacity="0.1" imageUrl={companyIconUrl} />
        <Tile.Front>
          <Grid
            ref={ref}
            height="310px"
            padding="x1"
            paddingLeft="x1_5"
            templateColumns="100%"
            templateRows="50px auto 50px"
          >
            <Tile.FadeInAnimation>
              <Box width="85%">
                <Text typography="baseStrong" lineClamp={1}>
                  {companyName}
                </Text>
                <Text typography="xSmall" color="whiteRGBA" opacity={0.7}>
                  {marketCap}
                </Text>
                {isRecentSearchEntry && (
                  <Grid justifyContent="flex-start" paddingTop="x0_5">
                    <Badge tone="attention">New</Badge>
                  </Grid>
                )}
              </Box>
            </Tile.FadeInAnimation>
            <Tile.ScaleInAnimation>
              <Grid
                alignItems="center"
                justifyContent="center"
                templateRows="190px"
              >
                <Box width="190px" padding="x1">
                  <Snowflake points={snowflakePoints}>
                    <Snowflake.Labels />
                  </Snowflake>
                </Box>
              </Grid>
            </Tile.ScaleInAnimation>
            <Tile.FadeInAnimation>
              <Grid
                templateColumns="0.4fr 0.3fr auto"
                alignItems="flex-end"
                height="100%"
              >
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    {tickerSymbol}
                  </Text>
                  <Text typography="xSmall">{sharePrice}</Text>
                </Box>
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    7D
                  </Text>
                  <Text typography="xSmall">
                    <PNV value={return7D}>{return7DFormatted}</PNV>
                  </Text>
                </Box>
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    1Y
                  </Text>
                  <Text typography="xSmall">
                    <PNV value={return1Y}>{return1YFormatted}</PNV>
                  </Text>
                </Box>
              </Grid>
            </Tile.FadeInAnimation>
          </Grid>
        </Tile.Front>
        <Tile.Back>
          <Tile.FadeInAnimation>
            <Grid
              height="310px"
              padding="x1"
              paddingLeft="x1_5"
              templateColumns="100%"
              templateRows="40px auto 50px"
            >
              <Grid
                templateColumns="40px auto"
                justifyContent="space-between"
                alignItems="center"
                paddingRight="x6"
              >
                <CompanyIcon
                  website={companyIconUrl}
                  tickerSymbol={tickerSymbol}
                  size={24}
                />
                <Box marginRight="x0_5">
                  <Badge tone="default" maxWidth={`calc(${width}px - 140px)`}>
                    {industry}
                  </Badge>
                </Box>
              </Grid>
              <Tile.FadeInAnimation>
                <Box marginTop="x2">
                  <Text typography="xSmall" lineClamp={8}>
                    {description}
                  </Text>
                </Box>
              </Tile.FadeInAnimation>
              <Grid
                templateColumns="0.35fr 0.35fr auto"
                alignItems="flex-end"
                height="100%"
              >
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    P/E
                  </Text>
                  <Text typography="xSmall">{peRatio}</Text>
                </Box>
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    Growth
                  </Text>
                  <Text typography="xSmall">{growthEstimate}</Text>
                </Box>
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    Analyst Target
                  </Text>
                  <Text typography="xSmall">{analystPriceTarget}</Text>
                </Box>
              </Grid>
            </Grid>
          </Tile.FadeInAnimation>
        </Tile.Back>
        <Box position="absolute" top={12} right={14} width={35}>
          <WatchlistAddRemoveCompanyToggle
            companyId={companyId}
            tickerSymbol={tickerSymbol}
            styleType="gentle"
            minimal
            onClick={(e: MouseEvent) => {
              e.preventDefault();
            }}
          />
        </Box>
        {isMobileOrTablet && (
          <Box position="absolute" bottom={12} right={14} width={35}>
            <Grid
              borderRadius="x5"
              width="40px"
              height="40px"
              boxShadow="subtleBorder"
              backgroundColor="transparentSubtle"
            >
              <Tile.SideToggleButton>
                <InfoDescriptionSolid />
              </Tile.SideToggleButton>
            </Grid>
          </Box>
        )}
      </Tile>
    </RouterLink>
  );
};
